<script>
  import { ignore } from '../../../Common/tools';
  import { External, M } from '../../../external';
  import { onDestroy, onMount } from 'svelte';

  export let data = {};
  export let onChange = ignore;

  const EMAIL_TYPES = [
    { id: 0, text: M.CAMPAIGN },
    { id: 1, text: M.AUTOMATION }
  ];

  let settings, campaigns, automations;

  async function selectEmailType() {
    if (settings.emailType === EMAIL_TYPES[0].id) {
      settings.automationId = '0';
      settings.stepId = '0';
      settings.stepEmails = null;
      settings.stepLinks = null;
      settings.stepLink = '0';
      settings.stepLinkTerm = null;
      settings.campaigns = await getCampaigns();

      if (settings.campaigns) {
        await selectCampaign(true);
      }

    } else if (settings.emailType === EMAIL_TYPES[1].id) {
      settings.campaignId = '0';
      settings.campaignLinks = null;
      settings.campaignLink = '0';
      settings.campaignLinkTerm = null;
      settings.automations = await getAutomations();

      if (settings.automations) {
        let autoIndex = await selectAutomation(true);

        if (autoIndex > -1) {
          await selectStep(autoIndex);
        }
      }
    }
  }

  async function selectCampaign(init) {
    let index = campaigns.findIndex((camp) => {
      return camp.id === settings.campaignId;
    });
    if (index > -1) {
      settings.campaignLinks = await getCampaignLinks(index);
    }
    if (!init) {
      settings.campaignLink = '0';
    }
  }

  async function selectAutomation(init) {
    let index = automations.findIndex((auto) => {
      return auto.id === settings.automationId;
    });
    if (index > -1) {
      settings.stepEmails = await getEmailSteps(index);
    }
    if (!init) {
      settings.stepId = '0';
      settings.stepLinks = null;
    }
    return index;
  }

  async function selectStep(autoIndex) {
    if (autoIndex === -1) {
      autoIndex = automations.findIndex((auto) => {
        return auto.id === settings.automationId;
      });
    }
    let stepIndex = automations[autoIndex].stepEmails.findIndex((email) => {
      return email.id === settings.stepId;
    });
    if (stepIndex > -1) {
      settings.stepLinks = await getStepLinks(autoIndex, stepIndex);
    }
  }

  async function getCampaigns() {
    if (!campaigns) { //fetch, then cache
      campaigns = await makeRequest('getEmailCampaignNames', null).catch(() => {
        External.Tools.showError(M.NO_CAMPAIGNS_YET);
        settings.emailType = '0';
      });
    }
    return campaigns;
  }

  async function getCampaignLinks(index) {
    if (!campaigns[index].links) {
      campaigns[index].links = await makeRequest('getCampaignLinks', { id: settings.campaignId }).catch(() => {
        External.Tools.showError(M.NO_LINKS_IN_CAMPAIGN);
        settings.campaignId = '0';
      });
    }
    return campaigns[index].links;
  }

  async function getAutomations() {
    if (!automations) {
      automations = await makeRequest('getAutomationNames', null).catch(() => {
        External.Tools.showError(M.NO_AUTOMATIONS_YET);
        settings.emailType = '0';
      });
    }
    return automations;
  }

  async function getEmailSteps(index) {
    if (!automations[index].stepEmails) {
      automations[index].stepEmails = await makeRequest('getEmailSteps', { id: settings.automationId }).catch(() => {
        External.Tools.showError(M.NO_AUTOMATION_EMAILS);
        settings.stepId = '0';
      });
    }
    return automations[index].stepEmails;
  }

  async function getStepLinks(autoIndex, stepIndex) {
    if (!automations[autoIndex].stepEmails[stepIndex].links) {
      automations[autoIndex].stepEmails[stepIndex].links = await makeRequest('getEmailStepLinks', {
        id: settings.automationId,
        stepId: settings.stepId
      }).catch(() => {
        External.Tools.showError(M.NO_LINKS_IN_AUTOMATION_STEP);
        settings.stepLinks = null;
        settings.stepId = '0';
      });
    }
    return automations[autoIndex].stepEmails[stepIndex].links;
  }

  function makeRequest(action, params) {
    return new Promise((resolve, reject) => {
      External.Tools.makeWebsiteRequest({
        action: action,
        data: params,
        onOk: (resp) => {
          if (resp.data?.length) {
            resolve(resp.data);
          } else {
            reject();
          }
        }
      });
    });
  }

  function isValidSelection(selectValue) {
    return selectValue !== '0' && isValid(selectValue);
  }

  function isValid(value) {
    return value && value.length > 0;
  }

  $: if (settings && settings.loaded &&
    (isValidSelection(settings.campaignLink) || isValid(settings.campaignLinkTerm) || isValidSelection(settings.stepLink) || isValid(settings.stepLinkTerm))) {   //only save when a campaign or step link is selected or link search term is specified

    if(isValidSelection(settings.campaignLink)) {
      settings.campaignLinkTerm = null; //remove any previously entered value
    }

    if(isValidSelection(settings.stepLink)) {
      settings.stepLinkTerm = null;
    }

    data.settings[0] = {
      key: '0',
      value: JSON.stringify({
        emailType: settings.emailType,
        campaignId: settings.campaignId,
        campaignLink: settings.campaignLink,
        campaignLinkTerm: settings.campaignLinkTerm,
        automationId: settings.automationId,
        stepId: settings.stepId,
        stepLink: settings.stepLink,
        stepLinkTerm: settings.stepLinkTerm
      })
    };

    onChange();
  }
  onMount(async () => {
    if (!data.settings) {
      data.settings = [];
    }

    settings = data.settings[0] ? JSON.parse(data.settings[0].value) :
      {
        emailType: '0', //requires a string to bind to the "please select" option val
        campaignId: '0',
        campaignLink: '0',
        campaignLinkTerm: null,
        automationId: '0',
        stepId: '0',
        stepLink: '0',
        stepLinkTerm: null
      };

    await selectEmailType();

    settings.loaded = true;
  });

  onDestroy(() => {
    settings = campaigns = automations = null;
  });

</script>

{#if settings && settings.loaded}
  <div class='col20 f-left small-rules mb-25'>
    <h4 class='col20 f-left mt-15 mb-10 t-center' style='line-height:1.4;'>{M.TRIGGER_CLICK}</h4>
    <div class='popup-input-wrapper f-left mt-25'>
      <div class='info'>
        <h5>{M.SELECT_EMAIL_TYPE}</h5>
      </div>
      <select class='page-select' bind:value={settings.emailType} on:change={async () => { await selectEmailType(); }}>
        <option value='0' disabled>{M.PLEASE_SELECT}</option>
        {#each EMAIL_TYPES as type}
          <option value='{type.id}'>{type.text}</option>
        {/each}
      </select>
    </div>
    {#if settings.emailType === EMAIL_TYPES[0].id}
      {#if settings.campaigns && settings.campaigns.length}
        <div class='popup-input-wrapper f-left'>
          <div class='info'>
            <h5>{M.SELECT_A_CAMPAIGN}</h5>
          </div>
          <select class='page-select' bind:value={settings.campaignId}
                  on:change={async () => { await selectCampaign(); }}>
            <option value='0' disabled>{M.PLEASE_SELECT}</option>
            {#each campaigns as campaign}
              <option value='{campaign.id}'>{campaign.name}</option>
            {/each}
          </select>
        </div>
        {#if settings.campaignLinks && settings.campaignLinks.length}
          <div class='popup-input-wrapper f-left'>
            <div class='info'>
              <h5>{M.SELECT_A_LINK}</h5>
            </div>
            <select class='page-select' bind:value={settings.campaignLink}>
              <option value='0' disabled>{M.PLEASE_SELECT}</option>
              {#each settings.campaignLinks as link}
                <option value='{link}'>{link}</option>
              {/each}
              <option value=''>{M.ANY_LINK_CONTAINING}</option>
            </select>
            {#if settings.campaignLink === ''}
              <input type='text' bind:value={settings.campaignLinkTerm} class='modify transition-border mb-5'
                     placeholder='{M.ENTER_A_VALUE}' />
            {/if}
          </div>
        {/if}
      {/if}
    {:else if settings.emailType === EMAIL_TYPES[1].id}
      {#if settings.automations && settings.automations.length}
        <div class='popup-input-wrapper f-left'>
          <div class='info'>
            <h5>{M.SELECT_AN_AUTOMATION}</h5>
          </div>
          <select class='page-select' bind:value={settings.automationId}
                  on:change={async () => { await selectAutomation(); }}>
            <option value='0' disabled>{M.PLEASE_SELECT}</option>
            {#each settings.automations as auto}
              <option value='{auto.id}'>{auto.name}</option>
            {/each}
          </select>
        </div>
        {#if settings.stepEmails && settings.stepEmails.length}
          <div class='popup-input-wrapper f-left'>
            <div class='info'>
              <h5>{M.SELECT_AN_EMAIL}</h5>
            </div>
            <select class='page-select' bind:value={settings.stepId} on:change={async () => { await selectStep(-1); }}>
              <option value='0' disabled>{M.PLEASE_SELECT}</option>
              {#each settings.stepEmails as step}
                <option value='{step.id}'>{step.name}</option>
              {/each}
            </select>
          </div>
          {#if settings.stepLinks && settings.stepLinks.length}
            <div class='popup-input-wrapper f-left'>
              <div class='info'>
                <h5>{M.SELECT_A_LINK}</h5>
              </div>
              <select class='page-select' bind:value={settings.stepLink}>
                <option value='0' disabled>{M.PLEASE_SELECT}</option>
                {#each settings.stepLinks as link}
                  <option value='{link}'>{link}</option>
                {/each}
                <option value=''>{M.ANY_LINK_CONTAINING}</option>
              </select>
              {#if settings.stepLink === ''}
                <input type='text' bind:value={settings.stepLinkTerm} class='modify transition-border mb-5'
                       placeholder='{M.ENTER_A_VALUE}' />
              {/if}
            </div>
          {/if}
        {/if}
      {/if}
    {/if}
  </div>
{/if}
